/**
 * 🌐 Google Drive Service v2 — Drive-Only Dokumentenverwaltung
 * 
 * Alle Dokumente werden NUR in Google Drive gespeichert.
 * Metadaten = Dateiname + appProperties. Kein Firestore für Dokumente!
 * 
 * Dateiname-Format: {Kategorie}_{Lieferant}_{YYYY-MM-DD}_{NNN}.{ext}
 * Beispiel: Rechnung_Metro_2026-02-13_001.jpg
 * 
 * Struktur in Google Drive:
 *   📁 KioskApp-Dokumente/
 *     📄 Rechnung_Metro_2026-02-13_001.jpg
 *     📄 Lieferschein_Edeka_2026-02-10_002.pdf
 */

import { getGoogleAccessToken, isTokenValid, setGoogleAccessToken, refreshGoogleAccessToken } from './googleAuthToken';
import { getAccessToken, refreshAccessToken } from './apiService';
import { SERVER_URL } from '../config/serverConfig';

const DRIVE_API = 'https://www.googleapis.com/drive/v3';
const DRIVE_UPLOAD_API = 'https://www.googleapis.com/upload/drive/v3';
const APP_FOLDER_NAME = 'KioskApp-Dokumente';

let _appFolderId = null;

// ============================================================
// 🔑 TOKEN-MANAGEMENT (Server-Refresh + GIS Fallback)
// ============================================================

// Laufender Refresh wird geteilt: parallele Aufrufe warten auf denselben Promise,
// statt mehrere Refreshes/Consent-Popups gleichzeitig auszulösen
let _tokenRefreshPromise = null;

async function getValidAccessToken() {
  // 1. Prüfe ob aktueller Token noch gültig ist
  if (isTokenValid()) {
    return getGoogleAccessToken();
  }

  if (!_tokenRefreshPromise) {
    _tokenRefreshPromise = doTokenRefresh().finally(() => {
      _tokenRefreshPromise = null;
    });
  }
  return _tokenRefreshPromise;
}

async function doTokenRefresh() {
  console.log('🔑 Access Token nicht vorhanden/abgelaufen → versuche Server-Refresh...');

  // 2. Server-seitigen Refresh versuchen (nutzt Google Refresh Token)
  try {
    const token = await refreshGoogleAccessToken();
    if (token) {
      console.log('🔑 ✅ Access Token via Server erneuert');
      return token;
    }
  } catch (e) {
    console.warn('🔑 Server-Refresh fehlgeschlagen:', e.message);
  }

  // 3. GIS Token Client als Fallback (zeigt ggf. Consent-Popup)
  try {
    const { requestDriveToken } = await import('./googleAuthService');
    // Erst silent versuchen
    try {
      const token = await requestDriveToken(false);
      if (token) {
        setGoogleAccessToken(token);
        console.log('🔑 ✅ Access Token via GIS (silent) erneuert');
        return token;
      }
    } catch {
      // Silent fehlgeschlagen → mit Popup
    }
    const token = await requestDriveToken(true);
    if (token) {
      setGoogleAccessToken(token);
      console.log('🔑 ✅ Access Token via GIS (interaktiv) erneuert');
      return token;
    }
  } catch (error) {
    console.error('🔑 Token-Erneuerung fehlgeschlagen:', error);
  }

  throw new Error('Google Drive Authentifizierung fehlgeschlagen. Bitte erneut anmelden.');
}

async function authenticatedFetch(url, options = {}) {
  let token = await getValidAccessToken();

  const makeRequest = (t) => fetch(url, {
    ...options,
    headers: { ...options.headers, 'Authorization': `Bearer ${t}` },
  });

  let response = await makeRequest(token);

  if (response.status === 401) {
    console.log('🔑 401 → erneuere Token (dedupliziert)...');
    try {
      // Gleicher geteilter Refresh wie in getValidAccessToken —
      // parallele 401s lösen nur einen Refresh aus
      if (!_tokenRefreshPromise) {
        _tokenRefreshPromise = doTokenRefresh().finally(() => {
          _tokenRefreshPromise = null;
        });
      }
      const newToken = await _tokenRefreshPromise;
      if (newToken) {
        response = await makeRequest(newToken);
      }
    } catch (e) {
      console.error('🔑 Alle Token-Refresh-Versuche fehlgeschlagen:', e);
    }
  }

  return response;
}

// ============================================================
// 📁 ORDNER-MANAGEMENT
// ============================================================

export async function getOrCreateAppFolder() {
  if (_appFolderId) return _appFolderId;

  const searchResponse = await authenticatedFetch(
    `${DRIVE_API}/files?q=${encodeURIComponent(
      `name='${APP_FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`
    )}&fields=files(id,name)&spaces=drive`
  );

  if (!searchResponse.ok) {
    const err = await searchResponse.text().catch(() => '');
    throw new Error(`Drive API Fehler: ${searchResponse.status} ${err}`);
  }

  const data = await searchResponse.json();
  if (data.files?.length > 0) {
    _appFolderId = data.files[0].id;
    console.log('📁 App-Ordner gefunden:', _appFolderId);
    return _appFolderId;
  }

  const createResponse = await authenticatedFetch(`${DRIVE_API}/files`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: APP_FOLDER_NAME, mimeType: 'application/vnd.google-apps.folder' }),
  });
  if (!createResponse.ok) throw new Error(`Ordner erstellen fehlgeschlagen: ${createResponse.status}`);
  
  const folder = await createResponse.json();
  _appFolderId = folder.id;
  console.log('📁 App-Ordner erstellt:', _appFolderId);
  return _appFolderId;
}

// ============================================================
// 📋 DOKUMENTE AUFLISTEN
// ============================================================

/**
 * Listet Dokumente aus dem Drive-App-Ordner seitenweise auf.
 * Ruft `onPage` für jede Seite mit den bereits geparsten Dokumenten auf,
 * damit die UI die erste Seite sofort anzeigen und den Rest im Hintergrund
 * nachladen kann (statt zu blockieren bis alle Dokumente da sind).
 *
 * @param {((docs: Array, info: {done: boolean, total: number}) => void)|null} onPage
 * @param {number} [pageSize=200] - Drive-Seitengröße (max. 1000)
 * @returns {Promise<Array>} ALLE Dokument-Objekte
 */
export async function listDriveDocumentsPaged(onPage = null, pageSize = 200) {
  const folderId = await getOrCreateAppFolder();
  const size = Math.min(Math.max(parseInt(pageSize, 10) || 200, 1), 1000);
  let allDocs = [];
  let pageToken = null;

  do {
    const params = new URLSearchParams({
      q: `'${folderId}' in parents and trashed=false and mimeType!='application/vnd.google-apps.folder'`,
      fields: 'nextPageToken,files(id,name,mimeType,size,createdTime,modifiedTime,thumbnailLink,appProperties,md5Checksum)',
      pageSize: String(size),
      orderBy: 'createdTime desc',
    });
    if (pageToken) params.set('pageToken', pageToken);

    const response = await authenticatedFetch(`${DRIVE_API}/files?${params}`);
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Drive Listing fehlgeschlagen: ${response.status} ${err}`);
    }

    const data = await response.json();
    const pageDocs = (data.files || []).map(parseDriveFile);
    allDocs = allDocs.concat(pageDocs);
    pageToken = data.nextPageToken;

    if (onPage) {
      try { onPage(pageDocs, { done: !pageToken, total: allDocs.length }); }
      catch (e) { console.warn('📋 listDriveDocumentsPaged onPage-Fehler:', e); }
    }
  } while (pageToken);

  console.log(`📋 ${allDocs.length} Dokumente aus Drive geladen`);
  return allDocs;
}

/**
 * Listet ALLE Dokumente aus dem Drive-App-Ordner (in einem Rutsch).
 * @returns {Promise<Array>} Dokument-Objekte
 */
export async function listDriveDocuments() {
  return listDriveDocumentsPaged(null, 1000);
}

/**
 * Parst eine Drive-Datei in ein Dokument-Objekt.
 */
function parseDriveFile(file) {
  const props = file.appProperties || {};

  let category = props.category || '';
  let categoryId = props.categoryId || '';
  let supplier = props.supplier || '';
  let date = props.date || '';
  let notes = props.notes || '';
  let ocrText = props.ocrText || '';
  let tags = [];
  try { tags = props.tags ? JSON.parse(props.tags) : []; } catch (e) { tags = []; }
  let favorite = props.favorite === 'true';
  let expiryDate = props.expiryDate || '';

  // Fallback: Aus Dateinamen parsen
  if (!category) {
    const parsed = parseDocumentFileName(file.name);
    category = parsed.category;
    categoryId = parsed.categoryId;
    supplier = parsed.supplier;
    date = parsed.date;
  }

  if (!categoryId && category) {
    categoryId = category.toLowerCase().replace(/[^a-z0-9äöüß]/g, '-');
  }

  const token = getGoogleAccessToken();
  const isImage = file.mimeType?.startsWith('image/');

  return {
    id: file.id,
    name: file.name,
    title: file.name.replace(/\.\w+$/, '').replace(/_/g, ' '),
    fileName: file.name,
    mimeType: file.mimeType || '',
    fileSize: parseInt(file.size || '0', 10),
    createdAt: file.createdTime || '',
    updatedAt: file.modifiedTime || '',
    categoryId,
    categoryName: category,
    supplier,
    date: date || (file.createdTime ? file.createdTime.split('T')[0] : ''),
    notes,
    ocrText,
    tags,
    favorite,
    expiryDate,
    fileUrl: token ? `${DRIVE_API}/files/${file.id}?alt=media&access_token=${token}` : '',
    thumbnailUrl: isImage && token
      ? `${DRIVE_API}/files/${file.id}?alt=media&access_token=${token}`
      : (file.thumbnailLink || ''),
    // Googles vorgeneriertes Mini-Thumbnail — für die Grid-Kacheln, damit nicht
    // das Vollbild geladen/dekodiert werden muss (OOM-Schutz auf schwachen Geräten)
    thumbnailLink: file.thumbnailLink || '',
    driveFileId: file.id,
    md5Checksum: file.md5Checksum || '',
    storageBackend: 'gdrive',
  };
}

/**
 * Parst Metadaten aus einem Dateinamen.
 * Format: Kategorie_Lieferant_YYYY-MM-DD_NNN.ext
 */
function parseDocumentFileName(fileName) {
  const nameWithoutExt = fileName.replace(/\.\w+$/, '');
  const parts = nameWithoutExt.split('_');

  const dateIdx = parts.findIndex(p => /^\d{4}-\d{2}-\d{2}$/.test(p));

  if (dateIdx >= 1) {
    const category = parts[0] || 'Dokument';
    const supplier = dateIdx > 1 ? parts.slice(1, dateIdx).join('_') : '';
    const date = parts[dateIdx];
    return {
      category,
      categoryId: category.toLowerCase().replace(/[^a-z0-9äöüß]/g, '-'),
      supplier,
      date,
    };
  }

  return {
    category: parts[0] || 'Dokument',
    categoryId: (parts[0] || 'dokument').toLowerCase().replace(/[^a-z0-9äöüß]/g, '-'),
    supplier: parts.length > 1 ? parts.slice(1).join('_') : '',
    date: '',
  };
}

// ============================================================
// 📤 UPLOAD
// ============================================================

/**
 * Lädt eine Datei in den App-Ordner hoch.
 * Metadaten im Dateinamen + appProperties.
 */
export async function uploadDocumentToDrive(blob, fileName, mimeType, appProperties = {}) {
  console.log(`📤 Drive Upload: ${fileName} (${Math.round(blob.size / 1024)} KB)`);

  const appFolderId = await getOrCreateAppFolder();

  const metadata = {
    name: fileName,
    parents: [appFolderId],
    appProperties,
  };

  const boundary = '===KioskAppBoundary===';
  const delimiter = `\r\n--${boundary}\r\n`;
  const closeDelimiter = `\r\n--${boundary}--`;

  const metadataPart = delimiter +
    'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
    JSON.stringify(metadata);

  const blobPart = delimiter +
    `Content-Type: ${mimeType}\r\n` +
    'Content-Transfer-Encoding: base64\r\n\r\n';

  const base64Data = await blobToBase64(blob);
  const requestBody = metadataPart + blobPart + base64Data + closeDelimiter;

  const response = await authenticatedFetch(
    `${DRIVE_UPLOAD_API}/files?uploadType=multipart&fields=id,name,createdTime,mimeType,size,appProperties`,
    {
      method: 'POST',
      headers: { 'Content-Type': `multipart/related; boundary=${boundary}` },
      body: requestBody,
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error('📤 Drive Upload Fehler:', response.status, errorText);
    throw new Error(`Google Drive Upload fehlgeschlagen: ${response.status}`);
  }

  const fileData = await response.json();
  console.log('📤 ✅ Upload erfolgreich:', fileData.id, fileData.name);
  return { fileId: fileData.id, name: fileData.name };
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// ============================================================
// ✏️ METADATEN AKTUALISIEREN
// ============================================================

/**
 * Aktualisiert Dateiname und/oder appProperties.
 */
export async function updateDriveDocument(fileId, updates) {
  const body = {};
  if (updates.name) body.name = updates.name;
  if (updates.appProperties) body.appProperties = updates.appProperties;

  const response = await authenticatedFetch(
    `${DRIVE_API}/files/${fileId}?fields=id,name,appProperties`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) throw new Error(`Drive Update fehlgeschlagen: ${response.status}`);
  return response.json();
}

// ============================================================
// 📥 DOWNLOAD / URL / BLOB-CACHE
// ============================================================

// Blob-URL-Cache: fileId → ObjectURL (verhindert wiederholte Downloads)
const _blobUrlCache = new Map();

/**
 * Lädt ein Drive-Bild per Auth-Header und gibt eine Blob-URL zurück.
 * Ergebnis wird gecacht, damit Thumbnails nicht neu geladen werden müssen.
 */
export async function loadDriveImageUrl(fileId) {
  if (!fileId) return '';
  if (_blobUrlCache.has(fileId)) return _blobUrlCache.get(fileId);

  const response = await authenticatedFetch(`${DRIVE_API}/files/${fileId}?alt=media`);
  if (!response.ok) throw new Error(`Bild laden fehlgeschlagen: ${response.status}`);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  _blobUrlCache.set(fileId, url);
  return url;
}

/**
 * Lädt ein Drive-Bild und verkleinert es clientseitig auf ein
 * speicherschonendes Thumbnail — für die Grid-Kacheln.
 *
 * Warum nicht Googles `thumbnailLink` (lh3.googleusercontent.com)? Der ist im
 * WebView nicht per fetch ladbar: lh3 sendet keine CORS-Header, die Anfrage
 * scheitert am Preflight. Die Drive-API (`googleapis.com`) dagegen erlaubt
 * authentifizierte Cross-Origin-Fetches. Wir holen also das Vollbild, rechnen
 * es beim Dekodieren herunter und cachen nur das kleine Thumbnail — so liegen
 * nicht hunderte Vollbild-Bitmaps gleichzeitig im Speicher (OOM-Schutz).
 */
export async function loadDriveImageThumb(fileId, maxEdge = 320) {
  if (!fileId) return '';
  const cacheKey = `imgthumb:${fileId}`;
  if (_blobUrlCache.has(cacheKey)) return _blobUrlCache.get(cacheKey);

  const response = await authenticatedFetch(`${DRIVE_API}/files/${fileId}?alt=media`);
  if (!response.ok) throw new Error(`Bild laden fehlgeschlagen: ${response.status}`);
  const blob = await response.blob();

  let thumbBlob = blob;
  let bitmap;
  try {
    bitmap = await createImageBitmap(blob);
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = longest > maxEdge ? maxEdge / longest : 1;
    if (scale < 1) {
      const w = Math.max(1, Math.round(bitmap.width * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
      thumbBlob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.8)) || blob;
    }
  } catch (e) {
    thumbBlob = blob; // Dekodieren fehlgeschlagen → Originalbild
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
  }

  const url = URL.createObjectURL(thumbBlob);
  _blobUrlCache.set(cacheKey, url);
  return url;
}

/**
 * Holt einen Blob vom eigenen Server (JWT-Auth) mit einmaligem Token-Refresh
 * bei 401. Für Binärantworten (Thumbnails) — apiFetch liefert nur JSON.
 */
async function serverFetchBlob(path) {
  let token = getAccessToken();
  let resp = await fetch(`${SERVER_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (resp.status === 401) {
    try { await refreshAccessToken(); token = getAccessToken(); } catch { /* ignore */ }
    resp = await fetch(`${SERVER_URL}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  }
  return resp;
}

/**
 * Lädt das von Google Drive vorab generierte Thumbnail (thumbnailLink) — aber
 * NICHT direkt von lh3.googleusercontent.com (das ist CORS-blockiert und
 * scheitert im WebView), sondern über den eigenen Server-Proxy. Der holt das
 * winzige JPEG (~kB) mit dem Drive-Token und reicht es mit CORS durch.
 *
 * Spart bei PDFs den Voll-Download + pdf.js-Rendern und bei Fotos das Laden des
 * Vollbilds — auf schwachen Handys der entscheidende Unterschied beim Öffnen
 * der Dokumentenliste.
 * Eigener Cache-Key (`thumb:`), damit er nicht mit loadDriveImageUrl kollidiert.
 */
export async function loadDriveThumbnailUrl(fileId, thumbnailLink, size = 320) {
  if (!thumbnailLink) return '';
  const cacheKey = `thumb:${fileId}`;
  if (_blobUrlCache.has(cacheKey)) return _blobUrlCache.get(cacheKey);

  // id: läuft der Link ab (Google stellt ihn nur für einige Stunden aus), holt der Server damit einen frischen
  const path = `/documents/thumbnail?link=${encodeURIComponent(thumbnailLink)}&size=${size}&id=${encodeURIComponent(fileId || '')}`;
  const response = await serverFetchBlob(path);
  if (!response.ok) throw new Error(`Thumbnail laden fehlgeschlagen: ${response.status}`);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  _blobUrlCache.set(cacheKey, url);
  return url;
}

/**
 * Gibt gecachte Blob-URL zurück (synchron, nur wenn bereits geladen).
 */
export function getCachedBlobUrl(fileId) {
  return _blobUrlCache.get(fileId) || '';
}

/**
 * Cache leeren und alle Blob-URLs freigeben.
 */
export function clearBlobUrlCache() {
  for (const url of _blobUrlCache.values()) {
    URL.revokeObjectURL(url);
  }
  _blobUrlCache.clear();
}

export function getDriveFileUrlSync(fileId) {
  const token = getGoogleAccessToken();
  if (!token || !fileId) return '';
  return `${DRIVE_API}/files/${fileId}?alt=media&access_token=${token}`;
}

export async function getDriveFileUrl(fileId) {
  const token = await getValidAccessToken();
  return `${DRIVE_API}/files/${fileId}?alt=media&access_token=${token}`;
}

export async function downloadDriveFile(fileId) {
  const response = await authenticatedFetch(`${DRIVE_API}/files/${fileId}?alt=media`);
  if (!response.ok) throw new Error(`Drive Download fehlgeschlagen: ${response.status}`);
  return response.blob();
}

// ============================================================
// 🗑️ LÖSCHEN
// ============================================================

export async function deleteFromDrive(fileId) {
  console.log('🗑️ Drive Delete:', fileId);
  const response = await authenticatedFetch(`${DRIVE_API}/files/${fileId}`, { method: 'DELETE' });
  if (!response.ok && response.status !== 404) {
    throw new Error(`Drive Löschen fehlgeschlagen: ${response.status}`);
  }
  console.log('🗑️ ✅ Gelöscht');
}

// ============================================================
// 📊 SPEICHERPLATZ
// ============================================================

export async function getDriveStorageUsage() {
  try {
    const response = await authenticatedFetch(`${DRIVE_API}/about?fields=storageQuota`);
    if (!response.ok) throw new Error(`About API: ${response.status}`);
    const data = await response.json();
    return {
      used: parseInt(data.storageQuota?.usage || '0', 10),
      total: parseInt(data.storageQuota?.limit || '16106127360', 10),
    };
  } catch (error) {
    console.warn('📊 Storage Usage Fehler:', error);
    return { used: 0, total: 15 * 1024 * 1024 * 1024 };
  }
}

// ============================================================
// 🔧 UTILITIES
// ============================================================

export function isDriveAvailable() {
  return !!getGoogleAccessToken();
}

export function clearDriveCache() {
  _appFolderId = null;
  clearBlobUrlCache();
}
